import { useId, type ComponentPropsWithRef, type ReactNode } from "react";
import { RadioGroup } from "radix-ui";
import { CircleAlert } from "lucide-react";
import { cx } from "./cx";

const LABEL = "flex items-baseline gap-2 text-xs font-semibold uppercase tracking-[0.04em] text-muted m-0 mb-1.5 p-0";

export function FieldLabel({
  htmlFor,
  children,
  required = false,
  optional,
}: {
  htmlFor?: string;
  children: ReactNode;
  required?: boolean;
  optional?: string;
}) {
  return (
    <label htmlFor={htmlFor} className={LABEL}>
      {children}
      {required ? <span className="font-medium normal-case tracking-normal text-attention">required</span> : null}
      {optional ? <span className="font-normal normal-case tracking-normal">{optional}</span> : null}
    </label>
  );
}

export function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div id={id} className="mt-1.5 flex items-start gap-1.5 text-md text-attention">
      <CircleAlert size={13} strokeWidth={1.8} aria-hidden="true" className="mt-px" />
      <span>{children}</span>
    </div>
  );
}

export function FieldHint({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <div id={id} className="mt-1.5 text-md text-muted">
      {children}
    </div>
  );
}

const INPUT =
  "w-full min-w-0 rounded-lg border border-border bg-bg px-2.5 text-fg outline-none placeholder:text-muted focus-visible:border-focus focus-visible:outline-2 focus-visible:outline-offset-2 aria-invalid:border-attention disabled:bg-surface-2 disabled:text-muted";

export function inputClass(className?: string): string {
  return cx(INPUT, "h-8", className);
}

export function TextInput({ className, ...props }: ComponentPropsWithRef<"input">) {
  return <input className={inputClass(className)} {...props} />;
}

export function TextArea({ className, ...props }: ComponentPropsWithRef<"textarea">) {
  return <textarea className={cx(INPUT, "block min-h-[62px] resize-y py-[7px] leading-[1.4]", className)} {...props} />;
}

export function Select({ className, ...props }: ComponentPropsWithRef<"select">) {
  return <select className={cx(INPUT, "h-8 px-1.5", className)} {...props} />;
}

export interface TextFieldProps extends Omit<ComponentPropsWithRef<"input">, "id"> {
  label: string;
  required?: boolean;
  optional?: string;
  error?: string | null | undefined;
  hint?: ReactNode;
  multiline?: false;
}

export function TextField({ label, required = false, optional, error, hint, ...props }: TextFieldProps) {
  const id = useId();
  const describedBy = [error ? `${id}-err` : null, hint ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className="mt-3">
      <FieldLabel htmlFor={id} required={required} {...(optional ? { optional } : {})}>
        {label}
      </FieldLabel>
      <TextInput
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        {...props}
      />
      {error ? <FieldError id={`${id}-err`}>{error}</FieldError> : null}
      {hint ? <FieldHint id={`${id}-hint`}>{hint}</FieldHint> : null}
    </div>
  );
}

export interface ReasonFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string | null | undefined;
  placeholder?: string;
}

export function ReasonField({
  value,
  onChange,
  error,
  placeholder = "Recorded in the audit log with this change",
}: ReasonFieldProps) {
  const id = useId();
  return (
    <div className="mt-3">
      <FieldLabel htmlFor={id} required>
        Reason
      </FieldLabel>
      <TextArea
        id={id}
        value={value}
        placeholder={placeholder}
        aria-required="true"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      {error ? <FieldError id={`${id}-err`}>{error}</FieldError> : null}
    </div>
  );
}

export interface RadioCardOption<T extends string> {
  value: T;
  label: string;
  description?: string;
}

export interface RadioCardsProps<T extends string> {
  label: string;
  value: T;
  options: readonly RadioCardOption<T>[];
  onChange: (value: T) => void;
  error?: string | null | undefined;
  hint?: ReactNode;
}

export function RadioCards<T extends string>({ label, value, options, onChange, error, hint }: RadioCardsProps<T>) {
  const id = useId();
  return (
    <fieldset className="m-0 mt-3 min-w-0 border-0 p-0">
      <legend id={`${id}-legend`} className={LABEL}>
        {label}
      </legend>
      <RadioGroup.Root
        value={value}
        onValueChange={(v) => {
          const match = options.find((o) => o.value === v);
          if (match) onChange(match.value);
        }}
        aria-labelledby={`${id}-legend`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        className={cx("grid gap-0.5 rounded-lg p-0.5", error ? "ring-1 ring-attention" : undefined)}
      >
        {options.map((o) => (
          <RadioGroup.Item
            key={o.value}
            value={o.value}
            className="group grid w-full grid-cols-[16px_minmax(0,1fr)] items-start gap-[9px] rounded-[7px] px-2 py-1.5 text-left text-fg hover:bg-surface-2"
          >
            <span className="mt-0.5 grid size-3.5 place-items-center rounded-full border-[1.5px] border-border bg-bg group-data-[state=checked]:border-link group-data-[state=checked]:bg-link">
              <RadioGroup.Indicator className="block size-1.5 rounded-full bg-on-primary" />
            </span>
            <span>
              <b className="font-strong">{o.label}</b>
              {o.description ? (
                <small className="mt-px block text-sm leading-[1.35] text-muted">{o.description}</small>
              ) : null}
            </span>
          </RadioGroup.Item>
        ))}
      </RadioGroup.Root>
      {error ? <FieldError id={`${id}-err`}>{error}</FieldError> : null}
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </fieldset>
  );
}
